/** One asynchronous read per page, keyed by its arguments, with loading and error states; a stale answer is dropped when the key moves on. */
import { useEffect, useState } from 'react';

export interface QueryState<T> {
  readonly loading: boolean;
  readonly value: T | null;
  readonly error: Error | null;
}

export const useQuery = <T>(key: string, read: () => Promise<T | null>): QueryState<T> => {
  const [state, setState] = useState<QueryState<T>>({ loading: true, value: null, error: null });
  useEffect(() => {
    let current = true;
    setState({ loading: true, value: null, error: null });
    read()
      .then((value) => {
        if (current) {
          setState({ loading: false, value, error: null });
        }
      })
      .catch((error: unknown) => {
        if (current) {
          setState({ loading: false, value: null, error: error instanceof Error ? error : new Error(String(error)) });
        }
      });
    return () => {
      current = false;
    };
    // The key names every argument the read depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state;
};
